import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Globe, ShoppingCart, UtensilsCrossed, CalendarCheck, Facebook, Instagram, Linkedin, Twitter, Youtube, Star, Save, Loader2, CheckCircle2, LinkIcon, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LINK_GROUPS = [
  {
    title: 'Business Links',
    description: 'Your main business URLs — used in social posts, citations, and marketing',
    fields: [
      { key: 'website', label: 'Website', icon: Globe, placeholder: 'https://www.yourbusiness.com' },
      { key: 'order_url', label: 'Online Ordering', icon: ShoppingCart, placeholder: 'https://order.toasttab.com/your-business' },
      { key: 'menu_url', label: 'Menu', icon: UtensilsCrossed, placeholder: 'https://www.yourbusiness.com/menu' },
      { key: 'appointment_url', label: 'Booking / Appointments', icon: CalendarCheck, placeholder: 'https://www.yourbusiness.com/book' },
    ],
  },
  {
    title: 'Social Media',
    description: 'Your social media profile URLs',
    fields: [
      { key: 'social_facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://www.facebook.com/yourbusiness' },
      { key: 'social_instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://www.instagram.com/yourbusiness' },
      { key: 'social_linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://www.linkedin.com/company/yourbusiness' },
      { key: 'social_twitter', label: 'X (Twitter)', icon: Twitter, placeholder: 'https://x.com/yourbusiness' },
      { key: 'social_youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://www.youtube.com/@yourbusiness' },
      { key: 'social_tiktok', label: 'TikTok', icon: null, placeholder: 'https://www.tiktok.com/@yourbusiness' },
    ],
  },
  {
    title: 'Review Links',
    description: 'Direct links where customers leave reviews — used in review request emails',
    fields: [
      { key: 'review_link_google', label: 'Google Reviews', icon: Star, placeholder: 'https://g.page/yourbusiness/review' },
      { key: 'review_link_facebook', label: 'Facebook Reviews', icon: Facebook, placeholder: 'https://www.facebook.com/yourbusiness/reviews' },
      { key: 'review_link_yelp', label: 'Yelp Reviews', icon: Star, placeholder: 'https://www.yelp.com/writeareview/biz/yourbusiness' },
    ],
  },
];

export default function ClientBusinessLinks({ client, getHeaders }) {
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const hdrs = () => ({ ...getHeaders(), 'Content-Type': 'application/json' });

  const fetchLinks = useCallback(() => {
    fetch(`${API}/api/client/dashboard/business-links`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => { setLinks(d); setDirty(false); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const updateField = (key, value) => {
    setLinks(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    setSaved(false);
  };

  const saveLinks = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/business-links`, {
        method: 'PUT', headers: hdrs(), body: JSON.stringify(links),
      });
      if (res.ok) { setSaved(true); setDirty(false); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">Loading business links...</div>;

  const filledCount = Object.values(links).filter(v => v && v.trim()).length;
  const totalCount = LINK_GROUPS.reduce((sum, g) => sum + g.fields.length, 0);

  return (
    <div className="space-y-6" data-testid="business-links-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-blue-500" /> Business Links
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Manage your business URLs — used in social posts, citations, and marketing materials</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-blue-50 text-blue-600 text-xs">{filledCount}/{totalCount} filled</Badge>
          <Button onClick={saveLinks} disabled={saving || !dirty} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="save-links-btn">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : saved ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {LINK_GROUPS.map(group => (
        <Card key={group.title} className="bg-white border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-900">{group.title}</CardTitle>
            <p className="text-xs text-gray-400">{group.description}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {group.fields.map(f => {
              const Icon = f.icon;
              const value = links[f.key] || '';
              return (
                <div key={f.key} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    {Icon ? <Icon className="w-4 h-4 text-gray-500" /> : <Globe className="w-4 h-4 text-gray-500" />}
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <Input
                      type="url"
                      value={value}
                      onChange={e => updateField(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="bg-gray-50 border-gray-200 text-gray-800 text-sm h-9"
                      data-testid={`link-${f.key}`}
                    />
                  </div>
                  {value && (
                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 mt-5 flex-shrink-0" data-testid={`visit-${f.key}`}>
                      <Globe className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* Contextual Upsell Cards */}
      {(!links.website || !links.website.trim()) && (
        <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 border-0 overflow-hidden relative" data-testid="upsell-website">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Badge className="bg-white/20 text-white text-[10px] mb-3">Premium Service</Badge>
                <h3 className="text-base font-bold text-white mb-1">Need a Professional Website?</h3>
                <p className="text-sm text-blue-100 mb-3 max-w-lg">
                  We'll build you a stunning, SEO-optimized website that integrates seamlessly with your online ordering, booking system, and Google Business Profile.
                </p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-2xl font-black text-white">$99</span>
                  <span className="text-sm text-blue-200">/month</span>
                  <span className="text-xs text-blue-300 ml-2">+ $499 one-time setup</span>
                </div>
                <ul className="space-y-1.5 mb-4">
                  {['Mobile-optimized design', 'SEO built-in from day one', 'Online ordering & booking integration', 'Managed hosting & updates included'].map((item, i) => (
                    <li key={i} className="text-xs text-blue-100 flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-green-300 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <Button className="bg-white text-blue-700 hover:bg-blue-50 text-xs font-semibold px-5" data-testid="upsell-website-btn" onClick={() => window.open('mailto:support@localbrandingpro.com?subject=Website Build Inquiry&body=I\'m interested in having a professional website built for my business.', '_blank')}>
                  <Sparkles className="w-3 h-3 mr-1.5" /> Get Started
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(!links.social_facebook || !links.social_instagram) && (
        <Card className="bg-gradient-to-r from-pink-500 to-orange-500 border-0 overflow-hidden relative" data-testid="upsell-social">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-white mb-1">Missing Social Profiles?</h3>
                <p className="text-xs text-pink-100 max-w-md">
                  Businesses with active social media get 3x more leads. We can set up and manage your Facebook, Instagram, and Google Business posting — hands-free.
                </p>
              </div>
              <Button className="bg-white/20 hover:bg-white/30 text-white text-xs border border-white/30 flex-shrink-0" onClick={() => window.open('mailto:support@localbrandingpro.com?subject=Social Media Management Inquiry', '_blank')}>
                Learn More
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {(!links.review_link_google) && (
        <Card className="bg-gradient-to-r from-amber-500 to-yellow-500 border-0 overflow-hidden relative" data-testid="upsell-reviews">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-white mb-1">No Google Review Link?</h3>
                <p className="text-xs text-amber-100 max-w-md">
                  89% of customers read reviews before visiting. Add your Google review link and we'll automatically send review requests to your customers after every interaction.
                </p>
              </div>
              <Button className="bg-white/20 hover:bg-white/30 text-white text-xs border border-white/30 flex-shrink-0" onClick={() => { const el = document.querySelector('[data-testid="link-review_link_google"]'); if (el) { el.scrollIntoView({ behavior: 'smooth' }); el.focus(); } }}>
                Add It Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
